import React, { useEffect } from "react";
import { toast } from "sonner";
import PerformanceCard from "../../../../components/common/card/performanceCard";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import {
  ADD_MORE_COVERS,
  ADJUST_SUM_INSURED,
  CHANGE_PLAN,
  TRANSFER_POLICY,
  REMOVE_BANK_NAME,
  CHANGE_PLATE_NUMBER,
  CANCEL_POLICY,
  SBDRIVE_ACTIVATION,
} from "../motorPolicyDataFile";
// Travel insurance action types
import {
  CHANGE_PLAN_TRAVEL,
  ADD_MORE_COVERS_TRAVEL,
  ADD_PASSENGER_TRAVEL,
  MODIFY_PASSENGER_TRAVEL,
  CHANGE_TRIP_DATE_TRAVEL,
  CANCEL_POLICY_TRAVEL
} from "../../../travelInsurancePages/travelPolicyDataFile";
// Personal Cyber insurance action types
import {
  CANCEL_POLICY_CYBER_SECURITY
} from "../../../personalCyberInsurancePages/cyberSecurityPolicyDataFile";
// Smart Device insurance action types
import {
  CANCEL_POLICY_SMART_DEVICE
} from "../../../smartDeviceInsurancePages/smartDevicePolicyDataFile";
// Domestic Helper insurance action types
import {
  CANCEL_POLICY_DOMESTIC_HELPER
} from "../../../domesticHelperPages/domesticHelperPolicyDataFile";
// Personal Accident insurance action types
import {
  CANCEL_POLICY_PERSONAL_ACCIDENT
} from "../../../personalAccidentInsurancePages/personalAccidentPolicyDataFile";
// Home insurance action types
import {
  CANCEL_POLICY_HOME_INSURANCE
} from "../../../homeInsurancePages/homeInsurancePolicyDataFile";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";

const ManagePolicyTab = ({ cards, policyDetails }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { policy_id } = useParams();
  const translate = useLanguageTranslate();

  // Check for endorsement success toast on component mount
  useEffect(() => {
    const endorsementSuccess = sessionStorage.getItem("endorsementSuccess");
    const endorsementMessage = sessionStorage.getItem("endorsementMessage");
    const endorsementMessageAr = sessionStorage.getItem("endorsementMessageAr");

    if (endorsementSuccess === "true") {
      // Clear the flag immediately to prevent showing again on refresh
      sessionStorage.removeItem("endorsementSuccess");
      sessionStorage.removeItem("endorsementMessage");
      sessionStorage.removeItem("endorsementMessageAr");

      // Show success toast with custom message or default
      const defaultMessage = "Your request has been submitted successfully!";
      const defaultMessageAr = "تم تقديم طلبك بنجاح!";

      toast.success(
        translate(
          endorsementMessage || defaultMessage,
          endorsementMessageAr || defaultMessageAr
        ),
        {
          position: "top-center",
          duration: 5000,
        }
      );
    }
  }, [translate]);

  const policyData = {
    PolicyNumber: policyDetails?.PolicyNumber ?? "",
    RegNo: policyDetails?.RegistrationNumber ?? "",
    status: policyDetails?.PolicyStatus ?? "",
    make: policyDetails?.Make ?? "",
    model: policyDetails?.Model ?? "",
    Plan: policyDetails?.PlanName ?? "",
    startDate: policyDetails?.FromDateStr ?? "",
    endDate: policyDetails?.ToDateStr ?? "",
    ChasissNumber: policyDetails?.ChasissNumber,
  };

  const id = policyData.PolicyNumber;
  const navigate = useNavigate();
  const encodePolicy = encodeURIComponent(id);
  
  // Detect if we're on a travel insurance page
  const isTravelInsurance = location.pathname.includes('/travel-insurance/');
  // Detect if we're on a personal cyber insurance page
  const isPersonalCyberInsurance = location.pathname.includes('/personal-cyber/');
  // Detect if we're on a smart device insurance page
  const isSmartDeviceInsurance = location.pathname.includes('/smart-device-insurance/');
  // Detect if we're on a domestic helper insurance page
  const isDomesticHelperInsurance = location.pathname.includes('/domestic-helper-insurance/');
  // Detect if we're on a personal accident page
  const isPersonalAccidentInsurance = location.pathname.includes('/personal-accident/');
  // Detect if we're on a home insurance page
  const isHomeInsurance = location.pathname.includes('/home-insurance/');

  const cardClickHandler = (card) => {
    console.log("🚀 Card clicked:", card.type, "isTravelInsurance:", isTravelInsurance, "isPersonalCyberInsurance:", isPersonalCyberInsurance, "isSmartDeviceInsurance:", isSmartDeviceInsurance, "isDomesticHelperInsurance:", isDomesticHelperInsurance, "isPersonalAccidentInsurance:", isPersonalAccidentInsurance, "isHomeInsurance:", isHomeInsurance);
    
    if (isHomeInsurance) {
      // Handle home insurance navigation - use original encrypted policy_id
      switch (card.type) {
        case CANCEL_POLICY_HOME_INSURANCE:
          navigate(`/home-insurance/endorsements/cancel-policy/${policy_id}`, {
            state: { policyData },
          });
          break;
        default:
          console.warn("Unknown home insurance action type:", card.type);
      }
    } else if (isDomesticHelperInsurance) {
      // Handle domestic helper insurance navigation - use original encrypted policy_id
      switch (card.type) {
        case CANCEL_POLICY_DOMESTIC_HELPER:
          navigate(`/domestic-helper-insurance/endorsements/cancel-policy/${policy_id}`, {
            state: { policyData },
          });
          break;
        default:
          console.warn("Unknown domestic helper insurance action type:", card.type);
      }
    } else if (isSmartDeviceInsurance) {
      // Handle smart device insurance navigation - use original encrypted policy_id
      switch (card.type) {
        case CANCEL_POLICY_SMART_DEVICE:
          navigate(`/smart-device-insurance/endorsements/cancel-policy/${policy_id}`, {
            state: { policyData },
          });
          break;
        default:
          console.warn("Unknown smart device insurance action type:", card.type);
      }
    } else if (isPersonalCyberInsurance) {
      // Handle personal cyber insurance navigation - use original encrypted policy_id
      switch (card.type) {
        case CANCEL_POLICY_CYBER_SECURITY:
          navigate(`/personal-cyber/endorsements/cancel-policy/${policy_id}`, {
            state: { policyData },
          });
          break;
        default:
          console.warn("Unknown personal cyber insurance action type:", card.type);
      }
    } else if (isPersonalAccidentInsurance) {
      // Handle personal accident navigation - use original encrypted policy_id
      switch (card.type) {
        case CANCEL_POLICY_PERSONAL_ACCIDENT:
          navigate(`/personal-accident/endorsements/cancel-policy/${policy_id}`, {
            state: { policyData },
          });
          break;
        default:
          console.warn("Unknown personal accident action type:", card.type);
      }
    } else if (isTravelInsurance) {
      // Handle travel insurance navigation
      switch (card.type) {
        case CHANGE_PLAN_TRAVEL:
          navigate(`/travel-insurance/change-plan/${encodePolicy}`);
          break;
        case ADD_MORE_COVERS_TRAVEL:
          navigate(`/travel-insurance/add-more-covers/${encodePolicy}`);
          break;
        case ADD_PASSENGER_TRAVEL:
          navigate(`/travel-insurance/add-passenger/${encodePolicy}`);
          break;
        case MODIFY_PASSENGER_TRAVEL:
          navigate(`/travel-insurance/modify-passenger/${encodePolicy}`);
          break;
        case CHANGE_TRIP_DATE_TRAVEL:
          navigate(`/travel-insurance/change-trip-date/${encodePolicy}`);
          break;
        case CANCEL_POLICY_TRAVEL:
          navigate(`/travel-insurance/cancel-policy/${encodePolicy}`, {
            state: { policyData },
          });
          break;
        default:
          console.warn("Unknown travel insurance action type:", card.type);
      }
    } else {
      // Handle motor insurance navigation (existing logic)
      if (card?.type == CHANGE_PLAN) {
        navigate(`/motor-insurance/change-plan/${encodePolicy}`);
      } else if (card.type == ADD_MORE_COVERS) {
        navigate(`/motor-insurance/add-more-covers/${encodePolicy}`);
      } else if (card.type == ADJUST_SUM_INSURED) {
        navigate(`/motor-insurance/adjust-sum-insured/${encodePolicy}`);
      } else if (card.type == REMOVE_BANK_NAME) {
        navigate(`/motor-insurance/remove-bank-name/${encodePolicy}`);
      } else if (card.type == TRANSFER_POLICY) {
        navigate(`/motor-insurance/transfer-ownership?policy=${encodePolicy}`);
      } else if (card.type == SBDRIVE_ACTIVATION) {
        navigate(`/motor-insurance/sbdriveactivation?policy=${encodePolicy}`);
      } else if (card.type == CHANGE_PLATE_NUMBER) {
        navigate(
          `/motor-insurance/change-plate-number?policy=${encodePolicy}&regNo=${policyData?.RegNo}&chassis=${policyData?.ChasissNumber}`
        );
      } else if (card.type == CANCEL_POLICY) {
        navigate(`/motor-insurance/cancel-policy/${encodePolicy}`, {
          state: { policyData },
        });
      }
    }
  };
  return (
    <div className="max767:p-5">
      <div className="grid grid-cols-3 items-start content-start gap-4 self-stretch flex-wrap ipad:w-full managepolicy max767:grid-cols-1 max767:gap-[8px]">
        {cards
          // Keep original CANCEL_POLICY filter
          .filter(
            (card) =>
              !(
                card.type === "CANCEL_POLICY" &&
                policyData.status !== "ACTIVE POLICY"
              )
          )
          // Additional filter for SBDrive
          .filter(
            (card) =>
              !(
                card.type === "SBDRIVE_ACTIVATION" &&
                ["TPL", "TPLUS"].includes(policyDetails?.PlanName?.trim().toUpperCase())
              )
          )
          // Travel insurance plan-based filters
          .filter((card) => {
            if (isTravelInsurance) {
              // First apply Individual vs Family filtering (from getFilteredTravelCards)
              const policyPlanType = policyDetails?.PlanType?.toLowerCase();
              const isIndividualPlan = policyPlanType === 'individual' || policyPlanType === 'single';
              
              console.log("=== TRAVEL POLICY CARD FILTERING ===");
              console.log("Policy Plan Type:", policyPlanType);
              console.log("Is Individual Plan:", isIndividualPlan);
              console.log("Card:", card.value, "Type:", card.type);

              // Apply Individual vs Family plan restrictions
              if (card.showForIndividual !== undefined || card.showForFamily !== undefined) {
                if (isIndividualPlan) {
                  if (card.showForIndividual === false) {
                    console.log(`Card "${card.value}" (${card.type}): RESTRICTED (individual plan)`);
                    return false;
                  }
                } else {
                  if (card.showForFamily === false) {
                    console.log(`Card "${card.value}" (${card.type}): RESTRICTED (family plan)`);
                    return false;
                  }
                }
              }

              // Now apply Silver/Gold/Platinum plan-based rules
              const planName = policyDetails?.PlanName;
              const planCode = policyDetails?.PlanCode;

              let tierPlanType = "silver"; // default
              if (planCode === "GO" || planName?.toLowerCase() === "gold") {
                tierPlanType = "gold";
              } else if (planCode === "PL" || planName?.toLowerCase() === "platinum" || planName?.toLowerCase() === "platinium" || planName?.toLowerCase().includes("platinum")) {
                tierPlanType = "platinum";
              }

              console.log("🎯 Tier filtering - Plan Name:", planName, "Plan Code:", planCode, "Tier Type:", tierPlanType);

              // CRITICAL: Hide "Change Plan" for Platinum plans
              if (tierPlanType === "platinum" && card.type === CHANGE_PLAN_TRAVEL) {
                console.log(`❌ Card "${card.value}" (${card.type}): RESTRICTED (platinum plan - cannot change from highest tier)`);
                return false;
              }

              // All other cards are allowed
              console.log(`✅ Card "${card.value}" (${card.type}): ALLOWED (${tierPlanType} plan)`);
              return true;
            }
            // For motor insurance, keep existing behavior
            return true;
          })
          .map((card, index) => (
            <PerformanceCard
              onClick={cardClickHandler}
              key={index}
              cardData={card}
              className="bg-[#F6F8F9] rounded-[10px] border border-solid border-[#DDE2E4] manage-policy-box flex items-center gap-4 p-6 cursor-pointer hover:rounded-[10px] hover:border hover:border-[#00719A] hover:bg-[#F6F8F9] hover:[box-shadow:0px_0px_1px_0px_rgba(26,_32,_36,_0.32),_0px_8px_16px_0px_rgba(91,_104,_113,_0.24)] ipad:w-full max767:flex max767:w-full max767:p-2 max767:border max767:border-solid max767:border-[#DDE2E4]"
            />
          ))}
      </div>
    </div>
  );
};

export default ManagePolicyTab;
